import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataService } from '@data/data.service';
import { ToolbarService } from '@toolbar/toolbar.service';
import { isJsObject, parseDec, trimString } from '@util/utils';
import { InputComponent } from '@input/input/input.component';
import { UserAutocompleteComponent } from '@input/user-autocomplete/user-autocomplete.component';
import { NotificationService } from '@notification/notification.service';
import { BackendPaths } from '@common/backend-paths';
import { NavigationService } from '@services/navigation.service';
import { StateService } from '@services/state.service';
import { InputFieldNames, InputFieldSizeType } from '@common/input-definitions';
import { ErrorElemContainer } from '@util/error-elem-container';
import { validateLength } from '@util/input-utils';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MyLog } from '@common/common';

@Component({
  selector: 'app-new-customer',
  templateUrl: './new-customer.component.html'
})
export class NewCustomerComponent implements OnInit {

  isDialog = false;

  blocked = false;

  deleted = false;

  partner: any;

  partnersUrl = BackendPaths.PARTNERS;

  id: any = '';

  data: any = {};

  types: any[] = [
    {
      id: 0, text: 'keine Anrede'
    },
    {
      id: 1, text: 'Herr'
    },
    {
      id: 2, text: 'Frau'
    },
    {
      id: 3, text: 'Firma'
    },
  ];

  routeSub!: Subscription;

  typeChangeSub!: Subscription;

  typeSelected: any;

  typesFC = new UntypedFormControl();

  addressLength!:InputFieldSizeType;
  cityLength!:InputFieldSizeType;
  firstNameLength!:InputFieldSizeType;
  lastNameLength!:InputFieldSizeType;
  phoneLength!:InputFieldSizeType;
  zipLength!:InputFieldSizeType;

  private _address!: InputComponent;
  @ViewChild('address', {
    read: InputComponent
  }) set address(address: InputComponent) {
    this._address = address;
  }

  private _city!: InputComponent;
  @ViewChild('city', {
    read: InputComponent
  }) set city(city: InputComponent) {
    this._city = city;
  }

  private _fname!: InputComponent;
  @ViewChild('fname', {
    read: InputComponent
  }) set fname(fname: InputComponent) {
    this._fname = fname;
  }

  private _lname!: InputComponent;
  @ViewChild('lname', {
    read: InputComponent
  }) set lname(lname: InputComponent) {
    this._lname = lname;
  }

  private _phone!: InputComponent;
  @ViewChild('phone', {
    read: InputComponent
  }) set phone(phone: InputComponent) {
    this._phone = phone;
  }

  private _zip!: InputComponent;
  @ViewChild('zip', {
    read: InputComponent
  }) set zip(zip: InputComponent) {
    this._zip = zip;
  }

  private _partnerRef!: UserAutocompleteComponent;
  @ViewChild('partnerRef', {
    read: UserAutocompleteComponent
  }) set partnerRef(partner: UserAutocompleteComponent) {
    this._partnerRef = partner;
  }

  constructor(
    private dataService: DataService,
    private navService: NavigationService,
    private notificationService: NotificationService,
    private stateService: StateService,
    private toolbarService: ToolbarService,
    private currentRoute: ActivatedRoute,
    @Optional() public dialogRef: MatDialogRef<NewCustomerComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
  ) {
    this.isDialog = isJsObject(dialogData) && !!dialogRef;
  }

  ngOnDestroy(): void {
    this.typeChangeSub.unsubscribe();
    if(this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  initSizes() {
    this.addressLength = this.stateService.getFieldSize(
      InputFieldNames.CUSTOMER_ADDRESS
    );
    this.cityLength = this.stateService.getFieldSize(
      InputFieldNames.CUSTOMER_CITY
    );
    this.firstNameLength = this.stateService.getFieldSize(
      InputFieldNames.CUSTOMER_FIRSTNAME
    );
    this.lastNameLength = this.stateService.getFieldSize(
      InputFieldNames.CUSTOMER_LASTNAME
    );
    this.phoneLength = this.stateService.getFieldSize(
      InputFieldNames.CUSTOMER_PHONE
    );
    this.zipLength = this.stateService.getFieldSize(
      InputFieldNames.CUSTOMER_ZIP
    );
  }

  ngOnInit(): void {
    this.initSizes();
    this.toolbarService.setTitle('Neuer Kunde');

    this.typeChangeSub = this.typesFC.valueChanges.subscribe((value: any) => {
      this.typeSelected = value;
    });

    if(this.isDialog) {
      this.id = 0;
    } else {
      this.routeSub = this.currentRoute.params.subscribe((param: Params) => {
        if (param.id) {
          this.id = parseDec(param.id);
          this.toolbarService.setTitle('Kundendaten editieren');
        }
        this.getData();
      });
    }

  }

  backendUrl() {
    return BackendPaths.CUSTOMERS_FULL + this.id;
  }

  getData() {
    if (this.id) {
      this.toolbarService.setTitle('Kunde editieren');

      const url = this.backendUrl();

      this.dataService.get(url).subscribe((data: any) => {
        const result = data?.result;
        const content = result?.data;

        if (isJsObject(content)) {
          this.data = content;
        }

        this.partner = this.data.partner;

        //delete this.data.partner;

        if (this.partner) {
          this.partner.fname = this.partner.first_name;
          this.partner.lname = this.partner.last_name;
        }

        if (Array.isArray(result?.statuses)) {
          this.types = result.statuses;
        }
        if(Number.isFinite(this.data.type)) {
          const typeOfUser = this.types.find((item:any)=> {
            return item.id === this.data.type;
          });
          if(typeOfUser) {
            this.typesFC.setValue(typeOfUser);
          }
        }

        this.blocked = !!this.data.blocked;
        this.deleted = !!this.data.deleted;
      });
    }
  }

  closeDialog(result = null) {
    this.dialogRef.close(result);
  }

  saveDialog() {
    this.save();
  }

  clickCancel() {
    this.navService.goBack();
  }

  clickSave() {
    this.save();
  }

  save() {
    this.data.first_name = trimString(this.data.first_name);
    this.data.last_name = trimString(this.data.last_name);
    this.data.phone = trimString(this.data.phone);
    this.data.zip = trimString(this.data.zip);
    this.data.address = trimString(this.data.address);

    if (this.typeSelected) {
      this.data.type = this.typeSelected.id;
    }

    const errorMessages: string[] = [];
    const errorElems = new ErrorElemContainer();

    if(this.data.phone && !validateLength(this.data.phone, this.phoneLength)) {
      errorElems.add(this._phone);
      errorMessages.push('Telefonnummer ist zu lang');
    }

    if (!this.data.first_name) {
      errorElems.add(this._fname);
      errorMessages.push('Vorname ist leer');
    } else if(!validateLength(this.data.first_name, this.firstNameLength)) {
      errorElems.add(this._fname);
      errorMessages.push('Vorname ist zu lang');
    }

    if (!this.data.last_name) {
      errorElems.add(this._lname);
      errorMessages.push('Nachname ist leer');
    } else if(!validateLength(this.data.last_name, this.lastNameLength)) {
      errorElems.add(this._lname);
      errorMessages.push('Nachname ist zu lang');
    }

    if(this.data.address && !validateLength(this.data.address, this.addressLength)) {
      errorElems.add(this._address);
      errorMessages.push('Straße ist zu lang');
    }

    if(this.data.zip && !validateLength(this.data.zip, this.zipLength)) {
      errorElems.add(this._zip);
      errorMessages.push('PLZ ist zu lang');
    }

    if(this.data.city && !validateLength(this.data.city, this.cityLength)) {
      errorElems.add(this._city);
      errorMessages.push('Stadt ist zu lang');
    }

    if ((!this.partner || !this.partner?.id)) {
      errorElems.add(this._partnerRef);
      errorMessages.push('Partner ist nicht gesetzt');
    }

    errorElems.highlightErrors();

    if (errorMessages.length > 0) {
      this.notificationService.showErrors(errorMessages);
    } else {
      this.data.partner = this.partner.id;

      const url = this.backendUrl();
      MyLog.log('SAVE:', this.data);

      this.dataService.post(url, this.data).subscribe((response: any) => {

        const result = response?.result;
        MyLog.log('SAVE-RES:', response);

        if (result?.success) {
          this.notificationService.showSuccess(
            'Kundendaten gespeichert'
          );

          if(this.isDialog && result.data) {
            this.closeDialog(result.data);
            return;
          } else {
            this.navService.goBack();
            return;
          }
        }

        let errors: any[] = [
          'Unbekannter Fehler beim Speichern'
        ];

        if (Array.isArray(result?.errors)) {
          errors = result.errors;
        }

        this.notificationService.showErrors(errors);
      });
    }
  }

}
